import { useState, useEffect } from "react";
import { toast } from "sonner";
import { logger } from "../lib/logger";
import { LOG_LEVELS_UI as LOG_LEVELS, LOG_CONFIG_DEFAULT } from "../lib/tabs";

const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN || "";
const adminHeaders = { "Content-Type": "application/json", "X-Admin-Token": ADMIN_TOKEN };

export default function DeveloperSettings({ readOnly = false }) {
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [queueEnabled, setQueueEnabled] = useState(true);
  const [logsConfig, setLogsConfig] = useState(LOG_CONFIG_DEFAULT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/admin/settings?order=key ASC", { headers: adminHeaders })
      .then(r => r.json())
      .then(rows => {
        const emailRow = rows.find(r => r.key === "email_enabled");
        if (emailRow) setEmailEnabled(emailRow.value === "true" || emailRow.value === "1");
        const queueRow = rows.find(r => r.key === "disable_queue");
        if (queueRow) setQueueEnabled(queueRow.value !== "true");

        const stored = localStorage.getItem("console_logs_config");
        if (stored) {
          setLogsConfig(JSON.parse(stored));
        } else {
          const lvl = {};
          LOG_LEVELS.forEach(l => {
            const row = rows.find(r => r.key === `console_log_${l.key}`);
            lvl[l.key] = row ? row.value === "true" : l.key === "error" || l.key === "warn";
          });
          const enabledRow = rows.find(r => r.key === "console_logs_enabled");
          const enabled = enabledRow ? enabledRow.value === "true" : true;
          setLogsConfig({ enabled, levels: lvl });
        }
      })
      .catch(() => {});
  }, []);

  async function saveSetting(key, value) {
    try {
      const rows = await fetch("/api/admin/settings?order=key ASC", { headers: adminHeaders }).then(r => r.json());
      const existing = rows.find(r => r.key === key);
      if (existing) {
        await fetch("/api/admin/settings", {
          method: "PATCH",
          headers: adminHeaders,
          body: JSON.stringify({ uid: existing.uid, value: String(value) }),
        });
      } else {
        await fetch("/api/admin/settings", {
          method: "POST",
          headers: adminHeaders,
          body: JSON.stringify({ key, value: String(value) }),
        });
      }
    } catch (err) {
      console.error("Erro ao salvar setting:", err);
    }
  }

  async function handleSave() {
    if (readOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try {
      await saveSetting("email_enabled", emailEnabled);
      await saveSetting("disable_queue", !queueEnabled);
      await saveSetting("console_logs_enabled", logsConfig.enabled);
      LOG_LEVELS.forEach(l => saveSetting(`console_log_${l.key}`, logsConfig.levels[l.key]));
      logger.setConfig(logsConfig);
    } finally {
      setSaving(false);
    }
  }

  function toggleLevel(key) {
    setLogsConfig(prev => ({
      ...prev,
      levels: { ...prev.levels, [key]: !prev.levels[key] },
    }));
  }

  return (
    <div data-id="page-developer-settings" className="max-w-2xl mx-auto space-y-8">
      <div>
        <h2 className="font-serif text-3xl text-softblack">Configurações de Desenvolvimento</h2>
        <p className="text-warmgray text-sm mt-1">Configurações técnicas do sistema</p>
      </div>

      <div className="bg-white border border-fendi rounded-xl p-6 space-y-6">
        <h3 className="font-serif text-xl text-softblack">Sistema de Email</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-softblack font-medium">Enviar emails</p>
            <p className="text-warmgray text-xs mt-1">Ativa/desativa o envio de emails do sistema</p>
          </div>
          <label className={`relative inline-flex items-center ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input type="checkbox" checked={emailEnabled} onChange={e => setEmailEnabled(e.target.checked)} className="sr-only peer" disabled={readOnly} />
            <div className="w-11 h-6 bg-fendi/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-champagne"></div>
          </label>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-softblack font-medium">Usar fila de email</p>
            <p className="text-warmgray text-xs mt-1">Desativa o uso da Cloudflare Queue, enviando emails directamente</p>
          </div>
          <label className={`relative inline-flex items-center ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input type="checkbox" checked={queueEnabled} onChange={e => setQueueEnabled(e.target.checked)} className="sr-only peer" disabled={readOnly} />
            <div className="w-11 h-6 bg-fendi/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-champagne"></div>
          </label>
        </div>
      </div>

      <div className="bg-white border border-fendi rounded-xl p-6 space-y-6">
        <h3 className="font-serif text-xl text-softblack">Logs da Consola</h3>

        {/* Global toggle */}
        <div className="flex items-center justify-between pb-4 border-b border-fendi/20">
          <div>
            <p className="text-softblack font-medium">Logs globais</p>
            <p className="text-warmgray text-xs mt-1">Ativa/desativa todos os logs do console</p>
          </div>
          <label className={`relative inline-flex items-center ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <input type="checkbox" checked={logsConfig.enabled} onChange={e => setLogsConfig(prev => ({ ...prev, enabled: e.target.checked }))} className="sr-only peer" disabled={readOnly} />
            <div className="w-11 h-6 bg-fendi/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-champagne"></div>
          </label>
        </div>

        {/* Per-level toggles */}
        <div className="space-y-3">
          <p className="text-[10px] uppercase tracking-widest text-warmgray font-medium">Níveis individuais</p>
          {LOG_LEVELS.map(l => (
            <div key={l.key} className={`flex items-center justify-between px-4 py-3 rounded-lg border ${l.bg} ${l.border}`}>
              <div>
                <p className={`text-sm font-medium ${l.color}`}>{l.label}</p>
              </div>
              <label className={`relative inline-flex items-center ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                <input type="checkbox" checked={logsConfig.levels[l.key]} onChange={() => toggleLevel(l.key)} className="sr-only peer" disabled={readOnly} />
                <div className="w-9 h-5 bg-white/50 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-champagne"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {!readOnly && (
        <button onClick={handleSave} disabled={saving} className="btn-primary text-xs !py-2 !px-6">
          {saving ? "A guardar..." : "Guardar"}
        </button>
      )}
    </div>
  );
}
